import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Business as BusinessIcon } from '@mui/icons-material';
import axios from 'axios';

const FirmManager = () => {
  const [firms, setFirms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('add'); // 'add' or 'edit'
  const [currentFirm, setCurrentFirm] = useState({
    name: '',
    contact_email: '',
    description: '',
    address: '',
    phone: '',
    website: ''
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Fetch firms on component mount
  useEffect(() => {
    fetchFirms();
  }, []);

  // Fetch firms from API
  const fetchFirms = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/audit/firms`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setFirms(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching firms:', err);
      setError('Failed to load firms. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Handle dialog open for adding a new firm
  const handleAddFirm = () => {
    setCurrentFirm({
      name: '',
      contact_email: '',
      description: '',
      address: '',
      phone: '',
      website: ''
    });
    setDialogMode('add');
    setOpenDialog(true);
  };

  // Handle dialog open for editing an existing firm
  const handleEditFirm = (firm) => {
    setCurrentFirm({ ...firm });
    setDialogMode('edit');
    setOpenDialog(true);
  };

  // Handle dialog close
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentFirm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      let response;
      
      if (dialogMode === 'add') {
        response = await axios.post(`${process.env.REACT_APP_API_URL}/audit/firms`, currentFirm, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        setSnackbar({
          open: true,
          message: 'Firm added successfully',
          severity: 'success'
        });
      } else {
        response = await axios.put(`${process.env.REACT_APP_API_URL}/audit/firms/${currentFirm.id}`, currentFirm, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        setSnackbar({
          open: true,
          message: 'Firm updated successfully',
          severity: 'success'
        });
      }
      
      // Refresh firms list
      fetchFirms();
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving firm:', err);
      setSnackbar({
        open: true,
        message: 'Error saving firm. Please try again.',
        severity: 'error'
      });
    }
  };

  // Handle firm deletion
  const handleDeleteFirm = async (id) => {
    if (window.confirm('Are you sure you want to delete this firm? This action cannot be undone.')) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_URL}/audit/firms/${id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        // Refresh firms list
        fetchFirms();
        
        setSnackbar({
          open: true,
          message: 'Firm deleted successfully',
          severity: 'success'
        });
      } catch (err) {
        console.error('Error deleting firm:', err);
        setSnackbar({
          open: true,
          message: err.response?.data?.message || 'Error deleting firm. Please try again.',
          severity: 'error'
        });
      }
    }
  };

  // Handle snackbar close
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({
      ...prev,
      open: false
    }));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1">
          <BusinessIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Firm Management
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddFirm}
        >
          Add Firm
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Contact Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {firms.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No firms found. Click "Add Firm" to create one.
                  </TableCell>
                </TableRow>
              ) : (
                firms.map((firm) => (
                  <TableRow key={firm.id}>
                    <TableCell>{firm.name}</TableCell>
                    <TableCell>{firm.contact_email}</TableCell>
                    <TableCell>{firm.phone || 'N/A'}</TableCell>
                    <TableCell>{firm.address || 'N/A'}</TableCell>
                    <TableCell>
                      <IconButton 
                        color="primary" 
                        onClick={() => handleEditFirm(firm)}
                        title="Edit firm"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        color="error" 
                        onClick={() => handleDeleteFirm(firm.id)}
                        title="Delete firm"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Firm Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogMode === 'add' ? 'Add New Firm' : 'Edit Firm'}
        </DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="name"
              label="Firm Name"
              name="name"
              value={currentFirm.name}
              onChange={handleInputChange}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="contact_email"
              label="Contact Email"
              name="contact_email"
              type="email"
              value={currentFirm.contact_email}
              onChange={handleInputChange}
            />
            <TextField
              margin="normal"
              fullWidth
              id="phone"
              label="Phone Number"
              name="phone"
              value={currentFirm.phone || ''}
              onChange={handleInputChange}
            />
            <TextField
              margin="normal"
              fullWidth
              id="address"
              label="Address"
              name="address"
              value={currentFirm.address || ''}
              onChange={handleInputChange}
            />
            <TextField
              margin="normal"
              fullWidth
              id="website"
              label="Website"
              name="website"
              value={currentFirm.website || ''}
              onChange={handleInputChange}
            />
            <TextField
              margin="normal"
              fullWidth
              id="description"
              label="Description"
              name="description"
              multiline
              rows={4}
              value={currentFirm.description || ''}
              onChange={handleInputChange}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {dialogMode === 'add' ? 'Add Firm' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FirmManager;
